/**
 * Posting one episode kit to Instagram, behind a confirm step that names the
 * account, shows the files in the order they will appear, and holds the caption
 * that goes with them. The backend re-derives the files and re-checks the
 * account before anything is created; this dialog then follows the post until
 * Instagram has it, and links to it.
 *
 * docs/FeatureDesigns/marketing/marketing-console-target.md, "Instagram"
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button, Modal } from '@shared-ui/components';
import type { WWEpisode } from '@shared-ui/whats-wrong/episodes.js';
import {
  marketingApi,
  type InstagramStatus,
  type MarketingAsset,
  type MusicTrack,
  type PublishKit,
  type SuggestedTrack,
  type PublishProgress,
} from '../../api/marketingApi';
import { AssetTile, MissingTile } from './MarketingParts';
import { SERIES } from './episodeContent';

const KIT_LABEL: Record<PublishKit, string> = { reel: 'reel', carousel: 'carousel post' };
const POLL_MS = 4000;

interface Props {
  episode: WWEpisode;
  kit: PublishKit;
  status: InstagramStatus;
  /** The listing the page already has, so the plan's paths preview as files. */
  assets: MarketingAsset[];
  onClose: () => void;
  /** After a post lands: refresh what the page knows is posted. */
  onPosted: () => void;
}

export function PublishInstagramDialog({ episode, kit, status, assets, onClose, onPosted }: Props) {
  const username = status.account?.username ?? '';
  const plan = useQuery({
    queryKey: ['instagram-plan', episode.id, kit],
    queryFn: () => marketingApi.instagramPlan(episode.id, kit, SERIES.id),
    staleTime: 0,
  });
  const [caption, setCaption] = useState(episode.caption);
  const [checked, setChecked] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [music, setMusic] = useState<MusicTrack | null>(null);
  // The episode's poll goes up as a caption add-on unless turned off here.
  const [withPoll, setWithPoll] = useState(!!episode.poll);
  const [pollOpen, setPollOpen] = useState(true);
  // One id per open dialog: a double-click returns the same job, never a second post.
  const requestId = useMemo(() => crypto.randomUUID(), []);

  const progress = useQuery({
    queryKey: ['instagram-publish', jobId],
    queryFn: () => marketingApi.instagramPublish(jobId!),
    enabled: !!jobId,
    refetchInterval: q => (isDone(q.state.data) ? false : POLL_MS),
  });
  const done = progress.data?.status === 'completed';
  useEffect(() => { if (done) onPosted(); }, [done, onPosted]);

  const already = (status.episodePosts?.[episode.id] ?? []).filter(p => p.type === kit);
  const byPath = (path: string) => assets.find(a => a.path === path);
  const limitLeft = status.limit ? status.limit.total - status.limit.used : null;

  const post = async () => {
    setSending(true);
    setSendError(null);
    try {
      const res = await marketingApi.publishToInstagram({
        series: SERIES.id, episode: episode.id, kit, caption, username, requestId,
        ...(kit === 'reel' && music ? { audio: { audioId: music.audioId, title: music.title, artist: music.artist } } : {}),
        ...(episode.poll && withPoll ? { poll: { options: episode.poll.options, keepOpen: pollOpen } } : {}),
      });
      setJobId(res.jobId);
    } catch (e) {
      setSendError((e as Error).message);
    } finally {
      setSending(false);
    }
  };

  const title = `Post #${episode.id} as a ${KIT_LABEL[kit]} to @${username}`;
  const footer = jobId
    ? <Button onClick={onClose}>{isDone(progress.data) ? 'Close' : 'Close (keeps posting)'}</Button>
    : (
      <>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={post} loading={sending} disabled={!checked || !plan.data || !caption.trim() || limitLeft === 0}>
          Post now
        </Button>
      </>
    );

  return (
    <Modal isOpen onClose={onClose} title={title} size="lg" footer={footer}>
      <div className="mk-publish">
        {jobId ? <Progress progress={progress.data} error={progress.error as Error | null} username={username} /> : (
          <>
            {already.length > 0 && (
              <p className="mk-warn">
                #{episode.id} is already on @{username} as a {KIT_LABEL[kit]}
                {already[0].timestamp && <> ({new Date(already[0].timestamp).toLocaleDateString()})</>}.
                {' '}Posting again makes a second copy.
                {already[0].permalink && <> <a href={already[0].permalink} target="_blank" rel="noreferrer">See it</a></>}
              </p>
            )}
            {limitLeft !== null && (
              <p className="mk-sub">{limitLeft} of {status.limit!.total} posts left in Instagram's 24-hour window.</p>
            )}

            <h3 className="mk-publish-h">Files, in order</h3>
            {plan.isLoading && <p className="mk-sub">Checking the files…</p>}
            {plan.error && <p className="mk-error">{(plan.error as Error).message}</p>}
            {plan.data && (
              <ol className="mk-slots">
                {[...plan.data.items, ...(plan.data.cover ? [plan.data.cover] : [])].map(item => {
                  const asset = byPath(item.path);
                  return (
                    <li key={item.path}>
                      {asset
                        ? <AssetTile asset={asset} label={item.role} />
                        : <MissingTile label={item.role} hint={item.path} />}
                    </li>
                  );
                })}
              </ol>
            )}
            {plan.data && plan.data.items.some(i => i.media === 'image') && (
              <p className="mk-sub">Stills go up as JPEG copies of these PNGs; Instagram takes JPEG only.</p>
            )}

            {kit === 'reel' && <MusicPicker selected={music} onSelect={setMusic} />}

            <label className="mk-publish-h" htmlFor="mk-caption">Caption</label>
            <textarea
              id="mk-caption"
              className="mk-caption"
              value={caption}
              onChange={e => setCaption(e.target.value)}
              rows={6}
              maxLength={2200}
            />
            <span className="mk-sub">{caption.length} / 2,200</span>

            {episode.poll && (
              <>
                <label className="mk-confirm">
                  <input type="checkbox" checked={withPoll} onChange={e => setWithPoll(e.target.checked)} />
                  <span>Add the poll. Instagram shows the caption as its question.</span>
                </label>
                {withPoll && (
                  <>
                    <ol className="mk-poll">
                      {episode.poll.options.map((o, i) => (
                        <li key={o}>{o}{i === episode.poll!.answer ? <span className="mk-sub"> (the answer)</span> : null}</li>
                      ))}
                    </ol>
                    <label className="mk-confirm">
                      <input type="checkbox" checked={pollOpen} onChange={e => setPollOpen(e.target.checked)} />
                      <span>Keep the poll open. Off, Instagram closes it after 3 days.</span>
                    </label>
                  </>
                )}
              </>
            )}

            <label className="mk-confirm">
              <input type="checkbox" checked={checked} onChange={e => setChecked(e.target.checked)} />
              <span>
                I've checked the files{kit === 'reel' ? ', the music' : ''}{episode.poll && withPoll ? ', the poll' : ''} and the caption.
                Post them to <strong>@{username}</strong>, publicly, now.
              </span>
            </label>
            {sendError && <p className="mk-error">{sendError}</p>}
          </>
        )}
      </div>
    </Modal>
  );
}

function isDone(p: PublishProgress | undefined): boolean {
  return !!p && (p.status === 'completed' || p.status === 'failed' || p.status === 'dead');
}

function Progress({ progress, error, username }: { progress?: PublishProgress; error: Error | null; username: string }) {
  if (error) return <p className="mk-error">Could not read the post's progress: {error.message}</p>;
  if (!progress || progress.status === 'queued') return <p>Queued. Uploading starts in a few seconds…</p>;
  if (progress.status === 'running') {
    return <p>Instagram is fetching and processing the files. Videos take up to a minute or two…</p>;
  }
  if (progress.status === 'completed') {
    return (
      <p className="mk-done">
        <Badge variant="success">Posted</Badge>{' '}
        It's live on @{username}.{' '}
        {progress.result?.permalink && <a href={progress.result.permalink} target="_blank" rel="noreferrer">Open the post</a>}
      </p>
    );
  }
  return (
    <div className="mk-error">
      <p><Badge variant="error">Not posted</Badge> {progress.error ?? 'The post failed.'}</p>
      <p className="mk-sub">
        Nothing is retried automatically, so it can never post twice. Check @{username} first: if the post is not there, close this and post again.
      </p>
    </div>
  );
}

/**
 * A reel's track from Instagram's own music library. Nothing is mixed into the
 * video: Instagram plays the track with the reel, as when one is picked in the
 * app. The series' own tracks come first, then tracks earlier reels used, with
 * the first series track picked; search covers the rest. Meta offers apps only
 * part of the library, so a track the app shows may be missing here.
 */
function MusicPicker({ selected, onSelect }: { selected: MusicTrack | null; onSelect: (t: MusicTrack | null) => void }) {
  const [input, setInput] = useState('');
  const [query, setQuery] = useState('');
  const [touched, setTouched] = useState(false);
  const suggested = useQuery({
    queryKey: ['instagram-music-suggested', SERIES.id],
    queryFn: () => marketingApi.instagramSuggestedMusic(SERIES.id),
    staleTime: 10 * 60 * 1000,
  });
  const search = useQuery({
    queryKey: ['instagram-music', query],
    queryFn: () => marketingApi.instagramMusic(query),
    enabled: !!query,
    staleTime: 10 * 60 * 1000,
  });
  const offered = (suggested.data?.tracks ?? []);
  const asTrack = (t: SuggestedTrack): MusicTrack | null => (t.available && t.audioId
    ? { audioId: t.audioId, title: t.title, artist: t.artist, durationMs: t.durationMs ?? 0, adsEligible: t.adsEligible ?? false, previewUrl: t.previewUrl, artworkUrl: t.artworkUrl }
    : null);

  // The series' first available track is the default, until the admin picks.
  useEffect(() => {
    if (touched || selected) return;
    const first = offered.map(asTrack).find(Boolean);
    if (first) onSelect(first);
  }, [offered, touched, selected, onSelect]);
  const pick = (t: MusicTrack | null) => { setTouched(true); onSelect(t); };

  const seriesTracks = offered.filter(t => t.source === 'series');
  const usedTracks = offered.filter(t => t.source === 'used');
  const results = (search.data?.tracks ?? []).filter(r => !offered.some(o => o.audioId === r.audioId));

  return (
    <div className="mk-music">
      <span className="mk-publish-h">Music</span>
      <ul className="mk-music-list">
        <li>
          <label className="mk-music-row">
            <input type="radio" name="mk-music" checked={!selected} onChange={() => pick(null)} />
            <span>No music <span className="mk-sub">(the reel is silent)</span></span>
          </label>
        </li>
        {suggested.isLoading && <li className="mk-sub">Finding the series' tracks…</li>}
        {suggested.error && <li className="mk-error">{(suggested.error as Error).message}</li>}
        {seriesTracks.length > 0 && <li className="mk-music-group">The series' tracks</li>}
        {seriesTracks.map(t => <TrackRow key={`s-${t.title}`} track={t} selected={selected} onPick={pick} asTrack={asTrack} />)}
        {usedTracks.length > 0 && <li className="mk-music-group">Used on earlier reels</li>}
        {usedTracks.map(t => <TrackRow key={`u-${t.audioId}`} track={t} selected={selected} onPick={pick} asTrack={asTrack} />)}
        {query && <li className="mk-music-group">Search: “{query}”</li>}
        {search.isLoading && <li className="mk-sub">Searching…</li>}
        {search.error && <li className="mk-error">{(search.error as Error).message}</li>}
        {results.map(t => (
          <TrackRow key={`r-${t.audioId}`} track={{ ...t, source: 'used', available: true }} selected={selected} onPick={pick} asTrack={asTrack} />
        ))}
        {query && !search.isLoading && !search.error && results.length === 0 && (
          <li className="mk-sub">Nothing more for “{query}”. Instagram offers apps only part of its library.</li>
        )}
      </ul>
      <form className="mk-music-search" onSubmit={e => { e.preventDefault(); setQuery(input.trim()); }}>
        <input value={input} onChange={e => setInput(e.target.value)} placeholder="Search Instagram's music for another track" aria-label="Search Instagram's music" />
        <Button type="submit" size="sm" variant="secondary" disabled={!input.trim()}>Search</Button>
      </form>
    </div>
  );
}

function TrackRow({ track, selected, onPick, asTrack }: {
  track: SuggestedTrack;
  selected: MusicTrack | null;
  onPick: (t: MusicTrack | null) => void;
  asTrack: (t: SuggestedTrack) => MusicTrack | null;
}) {
  const t = asTrack(track);
  const mmss = (ms: number) => `${Math.floor(ms / 60000)}:${String(Math.round(ms / 1000) % 60).padStart(2, '0')}`;
  return (
    <li>
      <label className={`mk-music-row${t ? '' : ' mk-music-row--off'}`}>
        <input type="radio" name="mk-music" disabled={!t} checked={!!t && selected?.audioId === t.audioId} onChange={() => onPick(t)} />
        {track.artworkUrl && <img src={track.artworkUrl} alt="" className="mk-music-art" />}
        <span className="mk-music-meta">
          <strong>{track.title}</strong>
          <span className="mk-sub">
            {track.artist}
            {track.durationMs ? ` · ${mmss(track.durationMs)}` : ''}
            {t ? (track.adsEligible === false ? ' · not for ads' : '') : ' · not offered to apps by Instagram today'}
          </span>
        </span>
        {track.previewUrl && <audio controls preload="none" src={track.previewUrl} className="mk-music-preview" />}
      </label>
    </li>
  );
}
